/**
 * Workout Templates — day-of-week exercise templates.
 *
 * Persisted to Supabase workout_templates table (server-first); falls back to
 * AsyncStorage when offline or on error.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('TEMPLATES');

const STORAGE_KEY_PREFIX = 'duel:workout_templates';

export interface TemplateExercise {
  exerciseId: string;
  exerciseName: string;
}

export interface DayTemplate {
  day: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  exercises: TemplateExercise[];
  updatedAt: string; // ISO
}

export type TemplateStore = Record<number | string, DayTemplate>;

let cache: TemplateStore | null = null;
let cacheUserId: string | null = null;

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

export const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Map a Supabase row into our TemplateStore shape */
function mapRowsToStore(rows: any[]): TemplateStore {
  const store: TemplateStore = {};
  for (const row of rows) {
    const key = row.day ?? row.id;
    store[key] = {
      day: row.day ?? -1,
      exercises: row.exercises ?? [],
      updatedAt: row.updated_at ?? new Date().toISOString(),
    };
  }
  return store;
}

export async function loadTemplates(userId?: string): Promise<TemplateStore> {
  const uid = userId ?? 'default';
  if (cache && cacheUserId === uid) return cache;

  // Try Supabase first
  try {
    const { data, error } = await supabase
      .from('workout_templates')
      .select('*')
      .eq('user_id', uid)
      .order('updated_at', { ascending: false });
    if (!error && data && data.length > 0) {
      cache = mapRowsToStore(data);
      cacheUserId = uid;
      return cache;
    }
  } catch {
    /* fall through to AsyncStorage */
  }

  // AsyncStorage fallback
  try {
    const raw = await AsyncStorage.getItem(storageKey(uid));
    cache = raw ? JSON.parse(raw) : {};
    cacheUserId = uid;
    return cache!;
  } catch {
    cache = {};
    cacheUserId = uid;
    return cache;
  }
}

export async function saveTemplate(day: number | string, exercises: TemplateExercise[], userId?: string): Promise<void> {
  const uid = userId ?? 'default';
  const store = await loadTemplates(uid);
  const entry: DayTemplate = {
    day: typeof day === 'number' ? day : -1,
    exercises,
    updatedAt: new Date().toISOString(),
  };
  store[day] = entry;
  cache = store;
  cacheUserId = uid;

  // Try Supabase first
  try {
    const { error } = await supabase
      .from('workout_templates')
      .upsert({
        id: `${uid}:${day}`,
        user_id: uid,
        day: typeof day === 'number' ? day : -1,
        exercises,
        updated_at: entry.updatedAt,
      }, { onConflict: 'id' });
    if (!error) {
      const label = typeof day === 'number' ? DAY_LABELS[day] : day;
      log.info('Template saved (Supabase) for', label, '—', exercises.length, 'exercises', '(user:', uid, ')');
      return;
    }
  } catch {
    /* fall through to AsyncStorage */
  }

  // AsyncStorage fallback
  await AsyncStorage.setItem(storageKey(uid), JSON.stringify(store));
  const label = typeof day === 'number' ? DAY_LABELS[day] : day;
  log.info('Template saved (AsyncStorage) for', label, '—', exercises.length, 'exercises', '(user:', uid, ')');
}

export async function getTemplateForDay(day: number, userId?: string): Promise<DayTemplate | null> {
  const store = await loadTemplates(userId);
  return store[day] ?? null;
}

export async function clearTemplate(day: number, userId?: string): Promise<void> {
  const uid = userId ?? 'default';
  const store = await loadTemplates(uid);
  delete store[day];
  cache = store;
  cacheUserId = uid;

  // Try Supabase first
  try {
    const { error } = await supabase
      .from('workout_templates')
      .delete()
      .eq('id', `${uid}:${day}`)
      .eq('user_id', uid);
    if (!error) return;
  } catch {
    /* fall through to AsyncStorage */
  }

  // AsyncStorage fallback
  await AsyncStorage.setItem(storageKey(uid), JSON.stringify(store));
}
